'use client';

import { useRef, useEffect } from 'react';
import Link from 'next/link';
import { gsap } from '@/lib/gsap';
import { ContactForm } from '@/components/contact/ContactForm';
import { company, addressLines } from '@/lib/company';

export function ContactPageClient() {
  const heroRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const formSectionRef = useRef<HTMLElement>(null);
  const projectsTitleRef = useRef<HTMLHeadingElement>(null);
  const projectsSubtitleRef = useRef<HTMLParagraphElement>(null);

  // Hover effect for title letters
  const applyHoverEffect = (letters: NodeListOf<Element>) => {
    const spansArray = Array.from(letters);

    const handleMouseMove = (e: Event) => {
      const mouseEvent = e as MouseEvent;
      const hoveredSpan = mouseEvent.target as HTMLElement;
      const rect = hoveredSpan.getBoundingClientRect();
      const mouseX = mouseEvent.clientX - rect.left;
      const scaleFactor = 0.2;
      const center = rect.width / 2;

      let scale: number;
      if (mouseX < center) {
        scale = (scaleFactor + 1) + (scaleFactor * mouseX) / center;
      } else {
        scale = (scaleFactor + 1) + (scaleFactor * (rect.width - mouseX)) / center;
      }

      gsap.to(hoveredSpan, { scaleY: scale, duration: 0.5, ease: 'power4.out' });

      const hoveredIndex = spansArray.indexOf(hoveredSpan);
      const prevSpan = spansArray[hoveredIndex - 1];
      const nextSpan = spansArray[hoveredIndex + 1];

      if (prevSpan) {
        const dist = Math.min(Math.abs(rect.left - mouseEvent.clientX), center);
        const scalePrev = 1 + (scaleFactor * (center - dist)) / center;
        gsap.to(prevSpan, { scaleY: scalePrev, duration: 0.5, ease: 'power4.out' });
      }

      if (nextSpan) {
        const dist = Math.min(Math.abs(rect.right - mouseEvent.clientX), center);
        const scaleNext = 1 + (scaleFactor * (center - dist)) / center;
        gsap.to(nextSpan, { scaleY: scaleNext, duration: 0.5, ease: 'power4.out' });
      }
    };

    const handleMouseLeave = () => {
      spansArray.forEach((span) => {
        gsap.to(span, { scaleY: 1, duration: 0.5, ease: 'power4.out' });
      });
    };

    spansArray.forEach((span) => {
      span.addEventListener('mousemove', handleMouseMove);
      span.addEventListener('mouseleave', handleMouseLeave);
    });
  };

  useEffect(() => {
    if (!heroRef.current) return;

    const ctx = gsap.context(() => {
      // Animate hero title letters
      const letters = titleRef.current?.querySelectorAll('.letter');
      if (letters) {
        gsap.set(letters, { scaleY: 0.3, opacity: 0 });

        const shuffledLetters = Array.from(letters).sort(() => Math.random() - 0.5);

        shuffledLetters.forEach((letter, index) => {
          gsap.to(letter, {
            duration: 0.6,
            scaleY: 1,
            opacity: 1,
            ease: 'power3.out',
            delay: 0.3 + index * 0.03,
          });
        });

        setTimeout(() => {
          applyHoverEffect(letters);
        }, 1500);
      }

      // Animate subtitle
      if (subtitleRef.current) {
        gsap.set(subtitleRef.current, { y: 30, opacity: 0 });
        gsap.to(subtitleRef.current, {
          y: 0,
          opacity: 1,
          duration: 0.7,
          delay: 1.2,
          ease: 'power3.out',
        });
      }

      // Animate projects CTA section on scroll
      if (projectsSubtitleRef.current) {
        gsap.set(projectsSubtitleRef.current, { y: 30, opacity: 0 });
        gsap.to(projectsSubtitleRef.current, {
          scrollTrigger: {
            trigger: projectsSubtitleRef.current,
            start: 'top 85%',
          },
          y: 0,
          opacity: 0.5,
          duration: 0.7,
          ease: 'power3.out',
        });
      }

      const projectsLetters = projectsTitleRef.current?.querySelectorAll('.projects-letter');
      if (projectsLetters) {
        gsap.set(projectsLetters, { scaleY: 0.3, opacity: 0 });

        gsap.to(projectsLetters, {
          scrollTrigger: {
            trigger: projectsTitleRef.current,
            start: 'top 80%',
          },
          scaleY: 1,
          opacity: 1,
          duration: 0.6,
          stagger: {
            each: 0.03,
            from: 'random',
          },
          ease: 'power3.out',
          onComplete: () => {
            applyHoverEffect(projectsLetters);
          },
        });
      }
    }, heroRef);

    return () => ctx.revert();
  }, []);

  const heroTitle = 'CONTACT';

  return (
    <div ref={heroRef}>
      {/* ─── Hero Section ─── */}
      <section
        // 100dvh (not min-h-screen/100vh): tracks the real visible viewport on
        // iOS so the hero fills the screen edge-to-edge with no gap above or
        // below the browser chrome.
        className="relative flex min-h-[100dvh] flex-col items-center justify-center overflow-hidden"
        style={{ backgroundColor: '#173835' }}
      >
        {/* Video Background */}
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            disablePictureInPicture
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source src="/videos/world-map.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-[#173835]/70" />
        </div>

        {/* Small label */}
        <span className="eyebrow relative z-[2]" style={{ marginBottom: '1.5rem' }}>
          Get in Touch
        </span>

        {/* Large CONTACT title with letter animation */}
        <h1
          ref={titleRef}
          className="font-six-caps text-[calc(1rem+20vw)] leading-[0.85] text-white lowercase text-center relative z-[2]"
        >
          {heroTitle.split('').map((char, i) => (
            <span
              key={i}
              className="letter inline-block"
              style={{ transformOrigin: 'center bottom' }}
            >
              {char}
            </span>
          ))}
        </h1>

        {/* Subtitle */}
        <p
          ref={subtitleRef}
          className="relative z-[2] text-center text-white/60 leading-relaxed"
          style={{
            marginTop: 'clamp(2.5rem, 6vw, 5rem)',
            fontSize: 'clamp(0.875rem, 1.5vw, 1.25rem)',
            maxWidth: '40rem',
            paddingLeft: '2rem',
            paddingRight: '2rem',
          }}
        >
          Tell us what you want to build, or ask about one of our products. Your message goes directly to the team.
        </p>

        {/* Bottom navigation */}
        <div
          className="absolute left-0 right-0 z-[2] flex items-end justify-between"
          style={{
            bottom: 'calc(clamp(2rem, 5vw, 3.5rem) + env(safe-area-inset-bottom, 0px))',
            paddingLeft: 'clamp(1.5rem, 4vw, 4rem)',
            paddingRight: 'clamp(1.5rem, 4vw, 4rem)',
          }}
        >
          <button
            onClick={() => formSectionRef.current?.scrollIntoView({ behavior: 'smooth' })}
            className="flex items-center cursor-pointer transition-opacity duration-300 hover:opacity-70"
            style={{ gap: '0.75rem', background: 'none', border: 'none', padding: 0 }}
          >
            <span className="uppercase text-white/40" style={{ fontSize: '11px', letterSpacing: '0.2em' }}>Scroll to Explore</span>
            <svg
              className="text-white/40"
              style={{ width: '14px', height: '14px' }}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          <a
            href="#connect"
            className="transition-opacity duration-300 hover:opacity-70 uppercase text-white/40"
            style={{ fontSize: '11px', letterSpacing: '0.2em', textDecoration: 'none' }}
          >
            Let&apos;s Connect
          </a>
        </div>
      </section>

      {/* ─── Two-Column: Contact Info + Form ─── */}
      <section
        ref={formSectionRef}
        id="connect"
        style={{
          backgroundColor: '#0c0c0c',
          paddingTop: 'clamp(5rem, 12vw, 10rem)',
          paddingBottom: 'clamp(5rem, 12vw, 10rem)',
          paddingLeft: 'clamp(1.5rem, 8vw, 120px)',
          paddingRight: 'clamp(1.5rem, 8vw, 120px)',
        }}
      >
        <div
          className="grid grid-cols-1 lg:grid-cols-2"
          style={{ maxWidth: '1200px', marginLeft: 'auto', marginRight: 'auto', gap: 'clamp(3rem, 8vw, 8rem)' }}
        >
          {/* ── Left Column: Info ── */}
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <p
              className="uppercase text-white/55"
              style={{ fontSize: '10px', letterSpacing: '0.3em', marginBottom: '1.5rem' }}
            >
              Get in Touch
            </p>

            <h2
              className="font-six-caps text-white lowercase"
              style={{ fontSize: 'clamp(5rem, 12vw, 10rem)', lineHeight: '0.85', marginBottom: 'clamp(2rem, 4vw, 3rem)' }}
            >
              {"LET'S TALK".split('').map((char, i) => (
                <span
                  key={i}
                  className={`inline-block ${char === ' ' ? 'mx-2 md:mx-4' : ''}`}
                  style={{ transformOrigin: 'center bottom' }}
                >
                  {char === ' ' ? '\u00A0' : char}
                </span>
              ))}
            </h2>

            <div style={{ width: '4rem', height: '1px', backgroundColor: 'rgba(255,255,255,0.1)', marginBottom: '2.5rem' }} />

            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              {/* Email */}
              <div>
                <p
                  className="uppercase text-white/55"
                  style={{ fontSize: '10px', letterSpacing: '0.3em', marginBottom: '0.5rem' }}
                >
                  Email
                </p>
                <a
                  href={`mailto:${company.email}`}
                  className="text-white hover:text-white/70 transition-colors"
                  style={{ fontSize: '1.0625rem', textDecoration: 'none' }}
                >
                  {company.email}
                </a>
              </div>

              {/* Phone */}
              <div>
                <p
                  className="uppercase text-white/55"
                  style={{ fontSize: '10px', letterSpacing: '0.3em', marginBottom: '0.5rem' }}
                >
                  Phone
                </p>
                <a
                  href={`tel:${company.telHref}`}
                  className="text-white hover:text-white/70 transition-colors"
                  style={{ fontSize: '1.0625rem', textDecoration: 'none' }}
                >
                  {company.phone}
                </a>
              </div>

              {/* Address */}
              <div>
                <p
                  className="uppercase text-white/55"
                  style={{ fontSize: '10px', letterSpacing: '0.3em', marginBottom: '0.5rem' }}
                >
                  Registered address
                </p>
                <p className="text-white" style={{ fontSize: '1.0625rem', lineHeight: '1.6' }}>
                  {addressLines[0]}<br />
                  {addressLines[1]}
                </p>
              </div>
            </div>
          </div>

          {/* ── Right Column: Form ── */}
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <ContactForm />
          </div>
        </div>
      </section>

      {/* ─── Registered office ─── */}
      <section
        className="self-styled relative overflow-hidden border-y border-white/10"
        style={{ backgroundColor: '#0a0a0a' }}
        aria-labelledby="registered-office-heading"
      >
        {/* Ghost wordmark — the jurisdiction, set like the footer mark. */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 flex items-center justify-center select-none">
          <span
            className="font-six-caps leading-none text-transparent"
            style={{
              fontSize: 'clamp(7rem, 20vw, 18rem)',
              letterSpacing: '0.08em',
              WebkitTextStroke: '1px rgba(255,255,255,0.06)',
            }}
          >
            ONTARIO
          </span>
        </div>

        <div className="content-padding relative z-10">
          <div className="grid grid-cols-1 gap-10 py-24 md:py-32 lg:grid-cols-[minmax(0,22rem)_1fr] lg:gap-20">
            <div>
              <span className="eyebrow">Registered office</span>
              <h2
                id="registered-office-heading"
                className="font-six-caps mt-6 uppercase leading-[0.88] text-white"
                style={{ fontSize: 'clamp(2.25rem, 5vw, 4rem)' }}
              >
                Where the
                <br />
                company sits
              </h2>
            </div>

            <div className="flex flex-col gap-8">
              <address className="max-w-md text-lg font-light not-italic leading-relaxed text-white/80 md:text-xl">
                {company.legalName}
                <br />
                {addressLines[0]}
                <br />
                {addressLines[1]}
              </address>

              <dl className="grid grid-cols-1 gap-x-10 sm:grid-cols-2">
                {[
                  { label: 'Jurisdiction', value: company.incorporatedIn },
                  { label: 'Corporation no.', value: company.corporationNumber },
                  { label: 'Governing law', value: company.governingLaw },
                  { label: 'Primary contact', value: company.contactName },
                ].map((row) => (
                  <div key={row.label} className="flex flex-col gap-2 border-t border-white/10 py-5">
                    <dt className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/40">
                      {row.label}
                    </dt>
                    <dd className="text-[15px] font-light text-white/85">{row.value}</dd>
                  </div>
                ))}
              </dl>

              <p className="max-w-lg text-sm font-light leading-relaxed text-white/45">
                We work with clients remotely and on site. Visits to the
                registered office are by appointment — email first and we will
                arrange it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Projects CTA Section ─── */}
      <section
        className="relative flex flex-col items-center justify-center"
        style={{
          backgroundColor: '#0c0c0c',
          paddingTop: 'clamp(5rem, 12vw, 10rem)',
          paddingBottom: 'clamp(5rem, 12vw, 10rem)',
        }}
      >
        <p
          ref={projectsSubtitleRef}
          className="text-center uppercase text-white/40"
          style={{
            fontSize: '11px',
            letterSpacing: '0.25em',
            marginBottom: 'clamp(1.5rem, 3vw, 2.5rem)',
          }}
        >
          Ready to see our work?
        </p>

        <Link href="/portfolio" className="group">
          <h2
            ref={projectsTitleRef}
            className="font-six-caps text-white lowercase text-center transition-all duration-500 group-hover:opacity-80"
            style={{
              fontSize: 'calc(1rem + 18vw)',
              lineHeight: '0.85',
              transform: 'scale(1)',
              transition: 'transform 0.5s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.3s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.02)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            {'PROJECTS'.split('').map((char, i) => (
              <span
                key={i}
                className="projects-letter inline-block"
                style={{ transformOrigin: 'center bottom' }}
              >
                {char}
              </span>
            ))}
          </h2>
        </Link>
      </section>

    </div>
  );
}
